/**
 * Generate a purchase URL with encoded parameters
 * @param baseUrl Base URL for the purchase web page
 * @param itemId ID of the item to be purchased
 * @param sessionToken Session token for user association (required)
 * @param affiliateCode Optional affiliate code for tracking
 * @param redirectUrl Optional URL to redirect after purchase
 * @returns Fully formed purchase URL
 */
export function generatePurchaseURL(
  baseUrl: string,
  itemId: string,
  sessionToken: string,
  affiliateCode?: string,
  redirectUrl?: string
): string {
  try {
    // Parse the base URL
    const url = new URL(baseUrl);
    
    // Extract the gameId from the existing query parameters
    const gameId = url.searchParams.get('gameId');
    
    // Clear existing search params
    url.search = '';
    
    // Set the correct path format: /store/items/{itemId}
    url.pathname = `/store/items/${itemId}`;
    
    // Add required parameters
    if (gameId) {
      url.searchParams.append('gameId', gameId);
    }
    
    // Always add the session token
    url.searchParams.append('sessionToken', sessionToken);
    
    // Add optional parameters if provided
    if (affiliateCode) {
      url.searchParams.append('affiliateCode', affiliateCode);
    }
    
    if (redirectUrl) {
      url.searchParams.append('redirectUrl', redirectUrl);
    }
    
    return url.toString();
  } catch (error) {
    console.error('Error generating purchase URL:', error);
    // Fallback to a simple format if URL parsing fails
    let url = `${baseUrl.split('?')[0]}/store/items/${itemId}?gameId=${encodeURIComponent(baseUrl.split('gameId=')[1]?.split('&')[0] || '')}`;
    url += `&sessionToken=${encodeURIComponent(sessionToken)}`;
    if (affiliateCode) url += `&affiliateCode=${encodeURIComponent(affiliateCode)}`;
    if (redirectUrl) url += `&redirectUrl=${encodeURIComponent(redirectUrl)}`;
    return url;
  }
}

/**
 * Parse parameters from a deep link URL
 * @param url URL to parse
 * @returns Object containing the parsed parameters
 */
export function parseDeepLinkParams(url: string): Record<string, string> {
  try {
    const parsedUrl = new URL(url);
    const params: Record<string, string> = {};
    
    // Extract all search parameters
    parsedUrl.searchParams.forEach((value, key) => {
      params[key] = value;
    });
    
    return params;
  } catch (error) {
    console.error('Error parsing URL:', error);
    return {};
  }
}

/**
 * Extract parameters from a store link
 * @param url URL to extract parameters from
 * @returns Object containing affiliateCode if present
 */
export function handleStoreLink(url: string): { affiliateCode?: string } {
  const params = parseDeepLinkParams(url);
  
  return {
    affiliateCode: params.affiliateCode
  };
}

/**
 * Generate a login URL for user authentication and session association
 * @param baseUrl Base URL for the login web page
 * @param sessionToken Session token to associate with the user
 * @param gameId Game ID for the session
 * @param redirectUrl Optional URL to redirect after login
 * @returns Fully formed login URL
 */
export function generateLoginURL(
  baseUrl: string,
  sessionToken: string,
  gameId: string,
  redirectUrl?: string
): string {
  try {
    // Parse the base URL
    const url = new URL(baseUrl);
    
    // Set the correct path format: /auth/login
    url.pathname = '/auth/login';
    
    // Add required parameters
    url.searchParams.append('sessionToken', sessionToken);
    url.searchParams.append('gameId', gameId);
    
    // Add optional parameters if provided
    if (redirectUrl) {
      url.searchParams.append('redirectUrl', redirectUrl);
    }
    
    return url.toString();
  } catch (error) {
    console.error('Error generating login URL:', error);
    // Fallback to a simple format if URL parsing fails
    return `${baseUrl.split('?')[0]}/auth/login?sessionToken=${encodeURIComponent(sessionToken)}&gameId=${encodeURIComponent(gameId)}${redirectUrl ? `&redirectUrl=${encodeURIComponent(redirectUrl)}` : ''}`;
  }
}

/**
 * Generate a tip URL for users to give a tip to the developer
 * @param baseUrl Base URL for the tip web page
 * @param sessionToken Session token to associate with the user
 * @param gameId Game ID for the session
 * @param amount Optional tip amount in cents
 * @param redirectUrl Optional URL to redirect after tipping
 * @returns Fully formed tip URL
 */
export function generateTipURL(
  baseUrl: string,
  sessionToken: string,
  gameId: string,
  amount?: number,
  redirectUrl?: string
): string {
  try {
    // Parse the base URL
    const url = new URL(baseUrl);
    
    // Set the correct path format: /store/tip
    url.pathname = '/store/tip';
    
    // Add required parameters
    url.searchParams.append('sessionToken', sessionToken);
    url.searchParams.append('gameId', gameId);
    
    // Add optional parameters if provided
    if (amount !== undefined) {
      url.searchParams.append('amount', amount.toString());
    }
    
    if (redirectUrl) {
      url.searchParams.append('redirectUrl', redirectUrl);
    }
    
    return url.toString();
  } catch (error) {
    console.error('Error generating tip URL:', error);
    // Fallback to a simple format if URL parsing fails
    let url = `${baseUrl.split('?')[0]}/store/tip?sessionToken=${encodeURIComponent(sessionToken)}&gameId=${encodeURIComponent(gameId)}`;
    if (amount !== undefined) url += `&amount=${encodeURIComponent(amount.toString())}`;
    if (redirectUrl) url += `&redirectUrl=${encodeURIComponent(redirectUrl)}`;
    return url;
  }
}

/**
 * Validate if a string is a valid URL
 * @param urlString String to validate as URL
 * @returns boolean indicating if the string is a valid URL
 */
export function isValidURL(urlString: string): boolean {
  try {
    new URL(urlString);
    return true;
  } catch (e) {
    return false;
  }
} 